import './index.css'
import get from 'lodash/get'
import * as React from 'react'
import type { CustomField, CustomFieldType } from 'types/business'
import type { Track } from 'types/track'
import { floatOnly, stringToBoolean, yearOnly } from '@disco/utils/string-utils'
import Row from 'components/common/row'
import CopyMetadataButton from 'components/common/tracks/copy-metadata-button'
import DialogFooter from 'components/modals/dialog-footer'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ContactUsLink from 'components/ui/contact-us-link'
import DatePicker from 'components/ui/date-picker'
import EmptyState from 'components/ui/empty-state'
import TextField from 'components/ui/text-field'
import Video from 'components/ui/video'
import FormField from '../metadata-fields/form-field'
import MetadataCheckbox from '../metadata-fields/metadata-checkbox'
import MetadataSelect from '../metadata-fields/metadata-select'

/**
 * CustomFields component for editing custom track metadata fields
 *
 * A React class component that provides an interface for editing custom business-specific
 * metadata fields. Supports various field types including text, number, date, dropdown,
 * checkbox, and year fields. Includes plan-specific access control and upgrade prompts.
 *
 * @param props - Component props
 * @param props.customFields - Array of custom field definitions organized in rows
 * @param props.saving - Whether metadata is currently being saved
 * @param props.showDifference - Whether to highlight field differences
 * @param props.hasUnsavedChanges - Whether there are unsaved changes
 * @param props.disableTrackOrder - Whether track order validation is disabled
 * @param props.saveTrackMetadata - Function to save track metadata
 * @param props.updateEditedTrack - Function to update edited track data
 * @param props.track - Original track data for reference
 * @param props.editedTrack - Track being edited with metadata
 * @param props.originalEditedTrackMetadata - Original metadata for comparison
 * @param props.isTrial - Whether user is on trial plan
 * @param props.customFieldsEnabled - Whether custom fields feature is enabled
 * @returns JSX element containing custom fields interface or upgrade prompt
 */
class CustomFields extends React.Component<{
  customFields: CustomField[][] | null | undefined
  saving: boolean
  showDifference?: boolean
  hasUnsavedChanges?: boolean
  disableTrackOrder?: boolean
  saveTrackMetadata: (...args: Array<any>) => any
  updateEditedTrack: (...args: Array<any>) => any
  track?: Track
  editedTrack: any
  originalEditedTrackMetadata?: any
  isTrial: boolean | null | undefined
  customFieldsEnabled: boolean | null | undefined
}> {
  _updateMetadataField: (
    key: string,
    value: null | string | Array<string>,
  ) => void = (key: string, value: null | string | string[]) => {
    const editedTrack = this.props.editedTrack
    const metadata = editedTrack.metadata
    this.props.updateEditedTrack({
      ...editedTrack,
      metadata: {
        ...metadata,
        [key]: { ...metadata[key], mixed: false, value },
      },
    })
  }
  _yearUpdate: (key: string, value: string | null | undefined) => void = (
    key: string,
    value: string | null | undefined,
  ) => {
    this._updateMetadataField(key, yearOnly(value || ''))
  }
  _numberUpdate: (key: string, value: string | null | undefined) => void = (
    key: string,
    value: string | null | undefined,
  ) => {
    this._updateMetadataField(key, floatOnly(value || ''))
  }
  _booleanSwitch: (key: string, value: boolean) => void = (
    key: string,
    value: boolean,
  ) => this._updateMetadataField(key, (!value).toString())
  _getUpdateFunction: (
    type: CustomFieldType | null | undefined,
  ) =>
    | ((key: string, value: string | null | undefined) => void)
    | ((key: string, value: null | string | Array<string>) => void) = (
    type: CustomFieldType | null | undefined,
  ) => {
    switch (type) {
      case 'number':
        return this._numberUpdate

      case 'year':
        return this._yearUpdate

      default:
        return this._updateMetadataField
    }
  }
  _getFieldComponent: (field: CustomField) => React.ReactNode = (
    field: CustomField,
  ) => {
    const { editedTrack } = this.props
    const maxLength = field.type === 'year' ? 4 : undefined

    const updateFunction = this._getUpdateFunction(field.type)

    const placeholderString =
      editedTrack.metadata[field.key] && editedTrack.metadata[field.key].mixed
        ? 'Mixed'
        : ''

    if (field.type === 'date') {
      return (
        <DatePicker
          compact={false}
          value={editedTrack.metadata[field.key]?.value}
          onChange={date => updateFunction(field.key, date || '')}
        />
      )
    }

    if (field.type === 'dropdown_multi' || field.type === 'dropdown_single') {
      return (
        <MetadataSelect
          placeholder={placeholderString}
          customField={field}
          field={editedTrack.metadata[field.key] || ''}
          onChange={options => {
            this._updateMetadataField(field.key, options)
          }}
          disabled={!field.editable}
        />
      )
    }

    return (
      <TextField
        value={editedTrack.metadata[field.key]?.value || ''}
        onChange={e => updateFunction(field.key, e.target.value)}
        placeholder={placeholderString}
        disabled={!field.editable}
        maxLength={maxLength}
        compact={false}
      />
    )
  }
  _handleSubmit: () => any = () => this.props.saveTrackMetadata()

  _validateTracks(): string {
    if (this.props.disableTrackOrder) {
      return ''
    }

    const { TrackNumber, Tracks } = this.props.editedTrack.metadata
    const hasTrackNumber = TrackNumber ? !!TrackNumber.value : false
    const hasTracks = Tracks ? !!Tracks.value : false

    if (!hasTrackNumber && hasTracks) {
      return 'Track number is required'
    } else {
      return ''
    }
  }

  _planMessage: () => React.ReactNode = () => {
    const { isTrial, customFieldsEnabled } = this.props
    let intercomMsg =
      'I’d like to discuss upgrading to access Shareable Channels'
    let buttonTitle = 'Upgrade to PRO'

    if (customFieldsEnabled) {
      intercomMsg = 'I’d like to discuss setting up custom fields'
      buttonTitle = 'Contact us to set up your fields'
    }

    return (
      <EmptyState>
        <Video
          src="https://www.youtube.com/embed/Ppa1njEQWQQ"
          width={1280}
          height={720}
        />
        <p className="form-note">
          Custom fields allow you to store more types of data on your tracks in
          DISCO. You can create fields to store more copyright information, the
          status of a track, or anything else that’s important to your business.
          Choose from checkboxes, text fields, dropdowns and more.
        </p>
        {isTrial ? (
          <Button
            appearance="primary"
            link="/settings/billing"
            caption="Sign up for PRO"
          />
        ) : (
          <ContactUsLink intercomMsg={intercomMsg} type="button">
            {buttonTitle}
          </ContactUsLink>
        )}
      </EmptyState>
    )
  }

  render(): null | React.ReactNode | React.ReactElement<'div'> {
    const {
      customFields,
      editedTrack,
      originalEditedTrackMetadata,
      showDifference,
      customFieldsEnabled,
    } = this.props

    if (
      // Show on pricing plans only if CUSTOM_FIELDS is true
      !customFields ||
      !customFieldsEnabled
    ) {
      return this._planMessage()
    }

    if (!customFields) return null
    return (
      <div className="CustomFields">
        {customFields.map((row, rowIndex) => {
          const columnsSize = row.map(r => r.field_size || 12)
          return (
            <Row
              className="Row_Baseline flc"
              key={rowIndex}
              columnsSize={columnsSize}
            >
              {row.map(field => {
                if (field.type === 'boolean') {
                  const fieldBooleanValue = stringToBoolean(
                    get(editedTrack.metadata[field.key], 'value', false),
                  )
                  return (
                    <MetadataCheckbox
                      className="CustomField_Checkbox"
                      key={field.key}
                      field={field}
                      checked={fieldBooleanValue}
                      onChange={() =>
                        this._booleanSwitch(field.key, fieldBooleanValue)
                      }
                    />
                  )
                }

                return (
                  <FormField
                    key={field.key}
                    component={this._getFieldComponent(field)}
                    showDifference={showDifference}
                    label={field.text || ''}
                    options={{
                      showIcon: showDifference,
                    }}
                    fieldName={field.key}
                    metadata={editedTrack.metadata}
                    originalMetadata={originalEditedTrackMetadata}
                  />
                )
              })}
            </Row>
          )
        })}
        <DialogFooter
          primary={
            <ButtonContainer>
              {!this.props.editedTrack.id &&
                this.props.editedTrack.trackIds && (
                  <CopyMetadataButton
                    trackIds={this.props.editedTrack.trackIds}
                  />
                )}
              {this.props.editedTrack.id && (
                <CopyMetadataButton trackIds={[this.props.editedTrack.id]} />
              )}
              <Button
                appearance="primary"
                onClick={this._handleSubmit}
                caption="Save metadata"
                disabled={
                  this.props.saving ||
                  !this.props.hasUnsavedChanges ||
                  !!this._validateTracks()
                }
                progressCaption="Saving metadata"
                progressIndicator={this.props.saving}
              />
            </ButtonContainer>
          }
        />
      </div>
    )
  }
}

export default CustomFields
